import React, { useState, useEffect } from "react";
import { FiX, FiChevronDown, FiChevronUp, FiMenu } from "react-icons/fi";
import Logo from "../../assets/images/bridgetune-logo.png";
import LogoWhite from "../../assets/images/bridgetune-logo-white.png";

import Button from "../library/button";
import { useLocation, useNavigate } from "react-router-dom";

interface SubLink {
  name: string;
  href: string;
}

interface NavLink {
  name: string;
  href?: string;
  active?: boolean;
  subPages?: SubLink[];
}

const navLinks: NavLink[] = [
  { name: "Home", href: "/" },
  {
    name: "Solutions",
    href: "/solutions",
  },
  { name: "Industries", href: "/industries" },
  { name: "Platform", href: "/platform" },
  {
    name: "Resources",
    subPages: [
      { name: "Resource Center", href: "/resources" },
      { name: "Success Stories", href: "/success-stories" },
      { name: "Blog", href: "/blogs" },
    ],
  },
  {
    name: "Company",
    subPages: [
      { name: "About-Us", href: "/about-us" },
      { name: "Careers", href: "/careers" },
    ],
  },
];

interface HeaderProps {
  homeHeaderDesign?: boolean;
}

const Header: React.FC<HeaderProps> = ({ homeHeaderDesign }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openMobileDropdown, setOpenMobileDropdown] = useState<string | null>(
    null,
  );

  const Menu = FiMenu as React.ComponentType<{
    color?: string;
    size?: number;
  }>;

  const location = useLocation();
  const navigate = useNavigate();

  const MenuCloseIcon = FiX as React.ComponentType<{
    className?: string;
  }>;

  const isLinkActive = (link: NavLink): boolean => {
    if (link.href === location.pathname) {
      return true;
    }
    if (link.subPages) {
      return link.subPages.some((sub) => sub.href === location.pathname);
    }
    return false;
  };

  useEffect(() => {
    if (!homeHeaderDesign) return;

    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [homeHeaderDesign]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const headerElement = document.getElementById("main-header");
      if (headerElement && !headerElement.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const ChevronDown = FiChevronDown as React.ComponentType<{
    className?: string;
  }>;
  const ChevronUp = FiChevronUp as React.ComponentType<{
    className?: string;
  }>;

  const handleSubrouting = (link: string) => {
    openDropdown === link ? setOpenDropdown(null) : setOpenDropdown(link);
  };
  return (
    <header
      id="main-header"
      className={`w-full fixed z-50 mx-auto flex items-center justify-between 
      py-2 lg:py-4 px-2 lg:px-[8%] rounded-b-[30px] lg:rounded-b-[174px]
      transition-all duration-300
      ${
        homeHeaderDesign
          ? scrolled
            ? "bg-[#2C73DB]/90 shadow-md"
            : "bg-transparent"
          : "bg-[#2C73DB]"
      }`}
    >
      {/* Logo */}
      <div
        className="lg:w-32 w-24 cursor-pointer"
        onClick={() => navigate("/")}
      >
        <img
          src={homeHeaderDesign && !scrolled ? Logo : LogoWhite}
          alt="Bridgetune Logo"
          className="w-full"
        />
      </div>

      {/* Desktop Nav */}
      <nav className="hidden lg:flex">
        <ul className="flex space-x-3 text-[.8rem] text-gray-400">
          {navLinks.map((link) => (
            <li
              key={link.name}
              className="relative"
              onClick={() => link.subPages && handleSubrouting(link.name)}
            >
              {link.href ? (
                <a
                  href={link.href}
                  className={`transition-all duration-300 cursor-pointer rounded-[6px] px-2.5 py-2 flex items-center 
                  hover:bg-[#ffffff1c] text-white ${
                    isLinkActive(link)
                      ? "bg-[#ffffff1c] font-medium"
                      : "bg-transparent"
                  }`}
                >
                  {link.name}
                </a>
              ) : (
                <button
                  className={`transition-all duration-300 cursor-pointer rounded-[6px] px-2.5 py-2 flex items-center gap-1
                  hover:bg-[#ffffff1c] text-white ${
                    isLinkActive(link) || openDropdown === link.name
                      ? "bg-[#ffffff1c] font-medium"
                      : "bg-transparent"
                  }`}
                  aria-expanded={openDropdown === link.name}
                >
                  {link.name}
                  {openDropdown === link.name ? (
                    <ChevronUp className="w-3 h-3 ml-1" />
                  ) : (
                    <ChevronDown className="w-3 h-3 ml-1" />
                  )}
                </button>
              )}

              {link.subPages && openDropdown === link.name && (
                <div className="absolute top-full mt-2 w-48 bg-white rounded-lg shadow-xl py-2 z-50 transition-opacity duration-200">
                  {link.subPages.map((subLink) => (
                    <a
                      key={subLink.name}
                      href={subLink.href}
                      onClick={() => setOpenDropdown(null)} // Close on click
                      className={`block px-4 py-2 text-sm text-[#1e1e1e] hover:bg-[#2c72db25] transition-colors
                        ${
                          subLink.href === location.pathname
                            ? "bg-[#2c72db25]"
                            : ""
                        }
                      `}
                    >
                      {subLink.name}
                    </a>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* Contact Button - Desktop */}
      <div className="hidden lg:block">
        <Button
          onClick={() => navigate("/contact-us")}
          className={`${
            homeHeaderDesign && !scrolled
              ? "bg-[#2C73DB] text-white "
              : "bg-white text-[#2C73DB]"
          } ${
            homeHeaderDesign ? "hover:bg-[#2C73DB]/90" : "hover:bg-[#fff]/80"
          }`}
        >
          Contact
        </Button>
      </div>

      {/* Mobile Menu Icon */}
      <div
        className={`lg:hidden text-lg z-[90] ${
          homeHeaderDesign && !scrolled && !menuOpen
            ? "text-white"
            : "text-[#2C73DB]"
        }`}
        onClick={() => setMenuOpen(!menuOpen)}
      >
        {menuOpen ? (
          <MenuCloseIcon className="w-8 h-8" />
        ) : (
          <Menu size={24} color="#fff" />
        )}
      </div>

      {/* Mobile Slide-out Menu */}
      <div
        className={`fixed top-0 left-0 h-full w-64 bg-white rounded-r-[40px] shadow-lg transform transition-transform duration-300 ease-in-out z-40 overflow-y-auto ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div
          className="w-36 cursor-pointer p-4"
          onClick={() => {
            navigate("/");
            setMenuOpen(false);
          }}
        >
          <img src={Logo} alt="Bridgetune Logo" className="w-full" />
        </div>
        <div className="p-4 flex flex-col space-y-1">
          {navLinks.map((link) => (
            <div key={link.name} className="flex flex-col">
              {link.href ? (
                <a
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  className={`text-sm text-[#2C73DB] rounded-[6px] p-2 transition-colors duration-200 ${
                    isLinkActive(link)
                      ? "bg-[#2c72db25] font-medium"
                      : "bg-transparent"
                  }`}
                >
                  {link.name}
                </a>
              ) : (
                <>
                  <button
                    onClick={() =>
                      setOpenMobileDropdown(
                        link.name === openMobileDropdown ? null : link.name,
                      )
                    }
                    className={`flex items-center justify-between text-sm text-[#2C73DB] rounded-[6px] p-2 transition-colors duration-200 ${
                      isLinkActive(link) || link.name === openMobileDropdown
                        ? "bg-[#2c72db25] font-medium"
                        : "bg-transparent"
                    }`}
                  >
                    {link.name}
                    {link.name === openMobileDropdown ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>

                  {/* Mobile Dropdown Content */}
                  {link.subPages && link.name === openMobileDropdown && (
                    <div className="flex flex-col pl-4 mt-1 space-y-1">
                      {link.subPages.map((subLink) => (
                        <a
                          key={subLink.name}
                          href={subLink.href}
                          onClick={() => setMenuOpen(false)}
                          className={`text-xs text-[#2C73DB] rounded-[6px] p-2 transition-colors duration-200 ${
                            subLink.href === location.pathname
                              ? "bg-[#2c72db25] font-semibold"
                              : "bg-transparent hover:bg-gray-100"
                          }`}
                        >
                          {subLink.name}
                        </a>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
          <div className="pt-2">
            <Button
              onClick={() => {
                navigate("/contact-us");
                setMenuOpen(false);
              }}
              className={"bg-[#2C73DB] text-white w-full"}
            >
              Contact
            </Button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-30 lg:hidden"
          onClick={() => setMenuOpen(false)}
        />
      )}
    </header>
  );
};

export default Header;
